import React from "react";

export const UITEXT = {
    RequesterInformation: {
        ExplanationNode: React.createElement(
            React.Fragment,
            null,
            `The Volvo Group company you selected in step
                        “Select Brand” ("the selected Volvo Group company") will process your personal data
                        for the purpose of verifying your identity.`,
                        React.createElement("br", null),
                        `The processing relies on its legitimate
                        interests to manage access requests under
                        Regulation (EU) 2023/2854. The copy of your
                        ID will be kept for 60 days after the
                        selected Volvo Group company has started
                        invoicing the third party data recipient
                        pursuant to the terms set out in the DSA
                        or up to 120 days after the request is assessed, 
                        whichever comes sooner.
                        The detailed information on the processing,
                        retention period and on how to exercise your
                        rights (for instance the right to access, to
                        object, to lodge complaint with the
                        supervisory authority) is available in the `,
            React.createElement(
                "a",
                {
                    href: "https://www.volvogroup.com/en/tools/privacy.html",
                    target: "_blank",
                    rel: "noopener noreferrer",
                },
                React.createElement(
                    "strong",
                    null,
                    React.createElement(
                        "u",
                        null,
                        "Customer Representative Privacy Notice",
                    ),
                ),
            ),
            " on Volvo website.",
        ),
        RequesterDSA: `I confirm that I have the right and authority to represent the company requesting the data.`,
    },
    ThirdPartyDataRecipientInformation: {
        Notice: ` You shall insert below the information about the natural or legal person requesting the data (the third party data recipient under the Data Act). Data access is subject to the approval of the user (owner / renter/ lessee) of the connected product.`,
        SME: " SME* or not-for-profit research organisation pursuant to article 9.4 of the Data Act",
        SME_Explanation:
            "*Small enterprise or medium-sized entreprise as defined in Article 2 of the Annex to Commission Recommendation 2003/361/EC",
        SME_Confirmation:
            "I confirm and warrant that the requesting company qualifies as a small and medium-sized enterprise or a not-for-profit research organization pursuant to art. 9.4 EU Data Act",
        Gatekeeper: `I certify that the requesting company  is not a gatekeeper pursuant to Article 3 of Regulation (EU) 2022/1925`,
    },
    UserInformation: {
        Notice: React.createElement(
            React.Fragment,
            null,
            "You shall insert below the information about the natural or legal person that the requesting company is acting on behalf of, and that owns the connected product(s) or to whom temporary rights to use that product(s) have been contractually transferred (the User under the Data Act).",
            React.createElement("br", null),
            "This information shall be consistent with the information in the customer portal.",
        ),
        CompanyName: React.createElement(
            React.Fragment,
            null,
            "Company name",
            React.createElement("br", null),
            "(User, the company owning or renting/leasing the product)",
        ),
    },
    DataSharingAgreement: {
        Notice: `Third party Data Sharing Agreement`,
        DSA: `By checking this box, I accept that the Data Sharing Agreement (DSA) available on the selected Volvo Group Company Data Act webpage applies to any data sharing under the Data Act:`,
        DSARTName: "For Renault Trucks",
        DSARTLink: "https://www.renault-trucks.com/en/data-act",
        DSAVBName: "For Volvo Buses",
        DSAVBLink: "https://www.volvobuses.com/data-act",
        DSAVTName: "For Volvo Trucks",
        DSAVTLink: "https://www.volvotrucks.com/data-act",
        DSAVEName: "For Volvo Energy",
        DSAVELink: "https://www.volvoenergy.com/en/tools/data-act.html",
        VG: `By checking this box, I acknowledge and accept that the selected Volvo Group company will be entitled to charge the requesting company in accordance with the Data Act, pursuant to the terms set out in the DSA.`,
        GDPR: `By checking this box, I confirm and warrant that the requesting company has a valid legal basis for any processing of personal data contained in the data it will access, as required under Regulation (EU) 2016/679 (GDPR)`,
    },
    ConnectedProductInformation: {
        Notice: `For verification purposes – additional information about one item from the user’s fleet of connected products is needed. Please provide information as requested below. Please note that you don’t have to submit a new request in order to access data from another connected product for the same user. Once the request is processed, access to the fleet connected products data is dependent on access granted by the fleet admin.`,
        ChooseProductLabel: `Select product identifier:`,
    },

    BrandLabel: `Brand`,
    BrandSelectionDescription: ` Select the brand of the connected product(s)
                            concerned by the access request`,
    RegisterFooterText: `Powered by Volvo Group`,
    AppTitle: `Data Act Request Tool`,
    ConfirmEmail: `Confirm e-mail`,
    ProductionEnvironment: "prod",
} as const;

export const TAB_NAMES = {
    RequestInformation: "You",
    ThirdPartyDataRecipientInformation: "Your company",
    UserInformation: "User",
    ConnectedProductInformation: "Fleet verification",
    DataSharingAgreement: "Data Sharing Agreement",
} as const;

export const STEP_STATUS = {
    Completed: "completed",
    Current: "current",
    NotCompleted: "not-completed",
} as const;

export const FORM_STEPS = {
    SignUp: "Sign up",
    Brand: "Brand",
    Request: "Request",
} as const;

export const API_ENDPOINTS = {
    SendVerificationEmail: "/api/send-verification-email",
    TokenVerification: "/api/token-verification",
    TokenEmailVerification: "/api/token-email-verification",
    CreateRequest: "/api/request",
    UploadFile: "/api/upload-file",
    Config: "/api/config",
    SupportRequest: "/api/support/request",
} as const;

export const UploadFileConfiguration = {
    AllowedTypes: `["application/pdf", "image/jpeg"]`,
    InputAllowedTypes: "application/pdf,image/jpeg,image/jpg",
    IncorrectFileTypeMessage: "Only PDF and JPEG files are allowed.",
    IncorrectFileSizeMessage: "File size must be 10MB or less.",
    UploadControlSizeAndTypeLabel: "PDF, JPEG file up to 10MB",
    MaxFileSize: 10 * 1024 * 1024, // 10MB in bytes
} as const;

export const VolvoFooter = {
    VolvoUrl: "https://www.volvogroup.com/",
    VolvoPrivacy: "https://www.volvogroup.com/en/tools/privacy.html",
} as const;

export const PRODUCT_TYPE_OPTIONS = [
    { error: false, disabled: false, label: "VIN", value: "VIN" },
    { error: false, disabled: false, label: "Chassis ID", value: "ChassisID" },
] as const;

export const SME_OPTIONS = [
    { error: false, disabled: false, label: "Yes", value: true },
    { error: false, disabled: false, label: "No", value: false },
] as const;

export const EU_COUNTRIES = [
    "Austria",
    "Belgium",
    "Bulgaria",
    "Croatia",
    "Cyprus",
    "Czechia",
    "Denmark",
    "Estonia",
    "Finland",
    "France",
    "Germany",
    "Greece",
    "Hungary",
    "Ireland",
    "Italy",
    "Latvia",
    "Lithuania",
    "Luxembourg",
    "Malta",
    "Netherlands",
    "Poland",
    "Portugal",
    "Romania",
    "Slovakia",
    "Slovenia",
    "Spain",
    "Sweden",
] as const;

export type EUCountry = typeof EU_COUNTRIES[number];

export const USER_FLEET_COUNTRIES = [
    "Austria",
    "Belgium",
    "Bulgaria",
    "Croatia",
    "Cyprus",
    "Czechia",
    "Denmark",
    "Estonia",
    "Finland",
    "France",
    "Germany",
    "Greece",
    "Hungary",
    "Ireland",
    "Italy",
    "Latvia",
    "Lithuania",
    "Luxembourg",
    "Malta",
    "Netherlands",
    "Poland",
    "Portugal",
    "Romania",
    "Slovakia",
    "Slovenia",
    "Spain",
    "Sweden",
] as const;

export type UserFleetCountry = typeof USER_FLEET_COUNTRIES[number];
